import { useContext, useEffect, useRef } from "react";
import {
  ageFeature,
  caseGroupingToPresentationType,
  genderFeature,
  usePatientCase,
} from "./usePatientCase";
import {
  CaseFindingPresentationType,
  FindingSource,
  MeasurementRange,
  PatientCaseGroupedFeature,
  QualifiedFeature,
  SavedCaseGroupInput,
} from "fe-common/graphql";
import {
  cleanPatientCaseForSave,
  ClinicModeContext,
  PatientCase,
  PatientCaseGrouping,
  shareIdQueryParams,
} from "../../context/diagnosticAgent";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { AGE, GENDER, GenderValue } from "shared";
import { useDiagnosticNavigation } from "./useDiagnosticNavigation";
import { v4 as uuid } from "uuid";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useUpdatedDD } from "./updateDDToState";
import { useUserStorage } from "fe-web-components/userStorage/useUserStorage";
import { getCurrentCase, setCurrentCase } from "../../updateCase/updateCaseMgr";
import { dedupQualifiers } from "fe-common/logic/qualifiedFeature/dedupQualifiers";
import { useRouteHelper } from "./useRouteHelper";
import { AgentRoute } from "./util/agentRoutes";
import { asQualifiedFeatureInput } from "fe-common/graphql/dao/daoTypesConverte";
import { useQueryParams } from "fe-common/useQueryParams";
import {__RouterContext} from "react-router";

function useSafeRouterLocation() {
  return useContext(__RouterContext)?.location;
}
const useQuery = () => {
  return new URLSearchParams(useSafeRouterLocation()?.search);
};

const patientCaseQfsToStore = [
  "panelsToAdd",
  "diseasesToAdd",
  "diseasesToIncludeInPlan",
  "diseasesToRemoveFromPlan",
];
export function patientCaseGroupingForSave(
  patientCase: PatientCase,
): SavedCaseGroupInput[] {
  const cleanPatientCase = cleanPatientCaseForSave(patientCase);

  const saveCaseInput: SavedCaseGroupInput[] = [];

  Object.values(PatientCaseGrouping).forEach((group) => {
    saveCaseInput.push({
      group,
      features: cleanPatientCase[group]
        .filter((qf) => !qf.skip)
        .map((qf) => {
          return {
            feature: asQualifiedFeatureInput(qf),
            source:
              (patientCase.savedGroupedFindings &&
                patientCase.savedGroupedFindings[group]?.find(
                  (origQf) => origQf.feature.id === qf.id,
                )?.source) ||
              FindingSource.Physician,
            group: caseGroupingToPresentationType(group),
          };
        }),
    });

    if (group === PatientCaseGrouping.BACKGROUND) {
      const age = ageFeature(cleanPatientCase.age);
      if (age) {
        saveCaseInput[saveCaseInput.length - 1].features.push({
          feature: asQualifiedFeatureInput(age),
          group: CaseFindingPresentationType.BackgroundFinding,
          source:
            (patientCase.savedGroupedFindings &&
              patientCase.savedGroupedFindings[group]?.find(
                (origQf) => origQf.feature.feature.id === age.feature.id,
              )?.source) ||
            FindingSource.Physician,
        });
      }

      const gender = genderFeature(cleanPatientCase.gender);
      if (gender) {
        saveCaseInput[saveCaseInput.length - 1].features.push({
          feature: asQualifiedFeatureInput(gender),
          group: CaseFindingPresentationType.BackgroundFinding,
          source:
            (patientCase.savedGroupedFindings &&
              patientCase.savedGroupedFindings[group]?.find(
                (origQf) => origQf.feature.feature.id === gender.feature.id,
              )?.source) ||
            FindingSource.Physician,
        });
      }
    }
    saveCaseInput[saveCaseInput.length - 1].features = saveCaseInput[
      saveCaseInput.length - 1
    ].features.filter((e) => e !== null);
  });

  saveCaseInput.push({
    group: "panelsToAdd",
    features: cleanPatientCase.panelsToAdd.map((qf) => ({
      feature: asQualifiedFeatureInput(qf),
      group: null,
      source: FindingSource.Physician,
    })),
  });

  patientCaseQfsToStore.forEach((g) => {
    saveCaseInput.push({
      group: g,
      features: cleanPatientCase[g].map((qf) => ({
        feature: asQualifiedFeatureInput(qf),
        group: null,
        source: FindingSource.Physician,
      })),
    });
  });

  return saveCaseInput;
}

export function ungroupPatientCase(
  restoredPatientCaseFindings: Record<string, PatientCaseGroupedFeature[]>,
): PatientCase {
  //Convert to Qualified feature (drop groupFeature data) for creating the patient case
  const patientCase: Partial<Record<
    string,
    QualifiedFeature[]
  >> = Object.values(PatientCaseGrouping).reduce((acc, group) => {
    if (restoredPatientCaseFindings[group]) {
      acc[group] = restoredPatientCaseFindings[group].map((gf) => gf.feature);
    }
    return acc;
  }, {});

  const ret = {} as PatientCase;

  const background = patientCase[PatientCaseGrouping.BACKGROUND];
  ret.gender = getGender(background);
  ret.age = getAge(background);

  // @ts-ignore
  ret.diseasesToAdd =
    patientCase.diseasesToAdd || patientCase.diseasesToConsider || []; //rename diseasesToConsider-->diseasesToAdd: diseasesToConsider are for old cases
  ret.panelsToAdd = patientCase.panelsToAdd || [];
  ret.diseasesToIncludeInPlan = patientCase.diseasesToIncludeInPlan || [];
  ret.diseasesToRemoveFromPlan = patientCase.diseasesToRemoveFromPlan || [];

  Object.values(PatientCaseGrouping).forEach((group) => {
    patientCase[group] &&
      patientCase[group].forEach((qf) => {
        qf.qualifiers = dedupQualifiers(qf.qualifiers);
      });
    ret[group] = patientCase[group] || [];
  });

  ret[PatientCaseGrouping.BACKGROUND] =
    background?.filter(
      (qf) => qf.feature.id !== GENDER && qf.feature.id !== AGE,
    ) || [];

  return ret;
}

function getGender(background: QualifiedFeature[]): GenderValue {
  const gender = background?.find((qf) => qf.feature.id === GENDER);
  if (gender) {
    return gender.mandatory?.value?.id as GenderValue;
  }
}

function getAge(background: QualifiedFeature[]): MeasurementRange {
  const age = background?.find((qf) => qf.feature.id === AGE);
  if (age) {
    return age.mandatory?.range as MeasurementRange;
  }
}

export const useGetShareId = () => {
  const clinicMode = useContext(ClinicModeContext);
  const queryParams = useQuery();
  const userStorage = useUserStorage();
  let patientCaseId = shareIdQueryParams
    .map((qp) => queryParams.get(qp))
    .find((shareId) => shareId);

  if (!patientCaseId && clinicMode) {
    const lastSharedCase = getCurrentCase(userStorage);
    patientCaseId = lastSharedCase.caseId as string;
  }

  return patientCaseId;
};

export const useStoredPatientCase = () => {
  const clinicMode = useContext(ClinicModeContext);
  const queryParams = useQueryParams();
  //Since agent paths do not support clinicID currently, get it from query param
  const clinicId = queryParams.get("clinicId");
  const { setPatientCase, restoredCaseID, handleReset } = usePatientCase();
  const dao = useDaoContext();
  const { setInWizard, inWizard } = useDiagnosticNavigation();
  const { loading, setLoading } = useUpdatedDD({});
  const userStorage = useUserStorage();

  let patientCaseId = useGetShareId();

  let currentSessionId = useRef<string>();

  const { navigateTo } = useRouteHelper();

  useEffect(() => {
    if (!loading && patientCaseId && patientCaseId !== restoredCaseID) {
      setLoading(true);
      (async () => {
        currentSessionId.current = uuid();
        let sessionId = currentSessionId.current;
        const patientCaseResponse = await dao.restorePatientCase(
          patientCaseId,
          clinicId,
        );
        if (currentSessionId.current === sessionId) {
          //create a patient case
          const patientCase = ungroupPatientCase(patientCaseResponse.findings);
          // Clear results
          handleReset();

          //keep last shareId in localStorage so we can update it on changes
          if (clinicMode) {
            setCurrentCase(
              patientCaseId,
              patientCaseResponse.etag,
              userStorage,
            );

            //Since agent patient case works with QualifiedFeature we lose the GroupFeature extra data such as source
            //So we store it here to be used when updating the same case
            patientCase.savedGroupedFindings = patientCaseResponse.findings;
          }
          // Load the case
          setPatientCase(patientCase, patientCaseId, false, false);

          evt({
            category: CATEGORY.AGENT,
            action: ACTION.RESTORE_CASE,
            label: patientCaseId,
          });

          // Set loaded cases as not in wizard if active finding exists
          // navigate to wizard in that case
          if (
            !patientCase.main?.length &&
            !patientCase.clinicalFeatures?.length
          ) {
            setInWizard(true);
            navigateTo({ route: AgentRoute.AgeAndGender });
          } else {
            // If loaded a case mark it as not in wizard
            setInWizard(false);
          }
        }
      })();
    }
  }, [
    clinicId,
    inWizard,
    patientCaseId,
    dao,
    setPatientCase,
    restoredCaseID,
    setInWizard,
    handleReset,
    setLoading,
    loading,
    clinicMode,
    navigateTo,
    userStorage,
  ]);
};
